import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { PartnerTrialService, VBP2024_PREMIUM_SUBSCRIPTION_ID } from '@galaxy/partner';
import { PARTNER_ID_INJECTION_TOKEN } from '../../core/constants';
import { Observable, catchError, firstValueFrom, of, switchMap, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StartTrialSuccessModalComponent } from '../start-trial-success-modal/start-trial-success-modal.component';

interface ButtonLoader {
  isLoading: boolean;
}

export interface DialogData {
  numDaysTrial: number;
}

@Component({
  selector: 'app-start-trial-modal',
  imports: [CommonModule, MatDialogModule, MatButtonModule, GalaxyButtonLoadingIndicatorModule, TranslateModule],
  templateUrl: './start-trial-modal.component.html',
  styleUrl: './start-trial-modal.component.scss',
})
export class StartTrialModalComponent {
  public startTrialBtnLoader: ButtonLoader = { isLoading: false };
  public disableButtonWhenLoading = true;
  public numDaysTrial;

  constructor(
    @Inject(PARTNER_ID_INJECTION_TOKEN) private partnerId$: Observable<string>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly partnerTrialService: PartnerTrialService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    public readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.numDaysTrial = data.numDaysTrial;
  }

  createTrial(): Observable<boolean> {
    return this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(startDate.getDate() + this.numDaysTrial);
        return this.partnerTrialService.createTrial(partnerId, VBP2024_PREMIUM_SUBSCRIPTION_ID, startDate, endDate);
      }),
      catchError((_) => {
        this.snackbarService.openErrorSnack(
          this.translateService.instant('START_TRIAL_MODAL.FAILED_TO_START_TRIAL'),
          {},
        );
        return of(false);
      }),
    );
  }

  async openSuccessModal(): Promise<void> {
    await firstValueFrom(
      this.dialog
        .open(StartTrialSuccessModalComponent, {
          data: { numDaysTrial: this.numDaysTrial },
        })
        .afterClosed(),
    );
  }

  async startTrial(buttonId: ButtonLoader): Promise<void> {
    buttonId.isLoading = true;
    const startTrialSuccess = await firstValueFrom(this.createTrial());
    buttonId.isLoading = false;
    await this.dialog.closeAll();
    if (startTrialSuccess) {
      await this.openSuccessModal();
      const queryParam = await firstValueFrom(this.route.queryParams);
      const targetPage = queryParam['page'] || '/dashboard';
      window.location.href = targetPage;
    } else {
      this.snackbarService.openErrorSnack(this.translateService.instant('START_TRIAL_MODAL.FAILED_TO_START_TRIAL'), {});
    }
  }
}
